import { createEmptyCompanyRevenueRange } from './types';
import { formatFundingSummary, getRevenueRangeString } from './utils';
const asRecord = (value) => value && typeof value === 'object' ? value : {};
const asString = (value, fallback = '') => (typeof value === 'string' ? value : fallback);
const asNumber = (value, fallback = 0) => typeof value === 'number' && !Number.isNaN(value) ? value : fallback;
const asNullableNumber = (value) => typeof value === 'number' && !Number.isNaN(value) ? value : null;
const asStringArray = (value) => {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.map(item => String(item));
};
const asLidString = (value) => {
    if (typeof value === 'number' && !Number.isNaN(value)) {
        return String(value);
    }
    if (typeof value === 'string' && value) {
        return value;
    }
    return '';
};
const mapTopics = (topics) => {
    if (!Array.isArray(topics)) {
        return [];
    }
    return topics
        .filter(topic => topic && typeof topic === 'object' && asString(topic.name))
        .map(topic => {
        var _a, _b, _c;
        const item = topic;
        return {
            name: item.name || '',
            score: asNumber(item.score),
            trend: {
                value: (_b = (_a = item.trend) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : null,
                direction: ((_c = item.trend) === null || _c === void 0 ? void 0 : _c.direction) || '',
            },
        };
    });
};
const mapRevenueRange = (revenueRange) => {
    const range = asRecord(revenueRange);
    if (!Object.keys(range).length) {
        return createEmptyCompanyRevenueRange();
    }
    const min = asNullableNumber(range.min);
    const max = asNullableNumber(range.max);
    return {
        min,
        max,
        string: asString(range.string) || getRevenueRangeString({ min, max }),
    };
};
const mapMetadata = (metadata) => {
    const value = asRecord(metadata);
    if (!Object.keys(value).length) {
        return null;
    }
    return {
        hasProspectingContacts: typeof value.has_prospecting_contacts === 'boolean' ? value.has_prospecting_contacts : null,
        hasProspectingContactsCount: asNullableNumber(value.has_prospecting_contacts_count),
        companyScore: asNullableNumber(value.company_score),
        partialCompany: typeof value.partial_company === 'boolean' ? value.partial_company : null,
    };
};
const getLocation = (company) => {
    const location = asRecord(company.location);
    if (Object.keys(location).length) {
        return location;
    }
    return asRecord(company.headquarter);
};
const getCompanySize = (company) => {
    var _a;
    const raw = (_a = company.company_size) !== null && _a !== void 0 ? _a : company.companySize;
    if (Array.isArray(raw)) {
        return { min: raw[0], max: raw[1] };
    }
    return asRecord(raw);
};
export const getCompanyMapKey = (company) => {
    if (company.companyLid != null) {
        return String(company.companyLid);
    }
    if (company.id) {
        return company.id;
    }
    if (company.fqdn) {
        return String(company.fqdn);
    }
    return '';
};
export const getSearchCompanyMapKey = (company) => {
    var _a;
    if (!company || typeof company !== 'object') {
        return '';
    }
    const record = company;
    const lid = asLidString((_a = record.company_lid) !== null && _a !== void 0 ? _a : record.companyLid);
    if (lid) {
        return lid;
    }
    return (asString(record.fqdn) ||
        asString(record.id) ||
        asString(record.homepage_url) ||
        asString(record.homepageUrl) ||
        asString(record.homePageUrl) ||
        asString(record.name));
};
export const mapSearchCompanyToCompany = (company) => {
    var _a, _b, _c;
    if (!company || typeof company !== 'object') {
        return null;
    }
    const record = company;
    const key = getSearchCompanyMapKey(record);
    if (!key) {
        return null;
    }
    const location = getLocation(record);
    const size = getCompanySize(record);
    const social = asRecord(record.social);
    const industry = asRecord(record.industry);
    const primaryIndustry = asRecord(industry.primary_industry);
    const subIndustry = asRecord(primaryIndustry.sub_industry);
    const companyLid = asNullableNumber((_a = record.company_lid) !== null && _a !== void 0 ? _a : record.companyLid);
    const specialitiesFromField = asStringArray(record.specialities);
    const specialities = specialitiesFromField.length ? specialitiesFromField : asStringArray(record.industry_tags);
    const companyLids = Array.isArray(record.company_lids)
        ? record.company_lids.map(item => Number(item)).filter(item => !Number.isNaN(item))
        : [];
    const mainIndustry = asString(primaryIndustry.key) || asString(record.primaryIndustry) || asString(record.mainIndustry);
    const linkedin = asString(social.LinkedIn) ||
        asString(social.linkedin) ||
        asString(asRecord(record.linkedin).url) ||
        asString(record.linkedin);
    return {
        id: key,
        hasCompany: true,
        name: asString(record.name),
        logoUrl: asString(record.logo_url) || asString(record.logoUrl),
        domain: asString(asRecord(record.domains).homepage) || 'Company Website',
        homePageUrl: asString(record.homepage_url) || asString(record.homePageUrl) || asString(record.homepageUrl),
        city: asString(location.city),
        region: asString(location.region) || asString(location.state),
        country: asString(location.country),
        country_iso2: asString(location.country_iso2) || asString(location.countryIso2),
        state: asString(location.state),
        min: asNumber(size.min),
        max: asNumber(size.max),
        about: asString(record.description) || asString(record.about),
        founded: record.founded || '',
        specialities,
        mainIndustry,
        mainIndustryKey: asString(primaryIndustry.key) || mainIndustry,
        mainIndustryId: asNullableNumber(primaryIndustry.id),
        subIndustry: asString(subIndustry.key) || asString(record.subIndustry),
        facebook: asString(social.facebook) || asString(asRecord(record.facebook).url) || asString(record.facebook),
        twitter: asString(social.twitter) || asString(asRecord(record.twitter).url) || asString(record.twitter),
        LinkedIn: linkedin,
        isSocial: Boolean(record.social || record.linkedin || record.facebook || record.twitter),
        companyResultString: JSON.stringify(company),
        revenueRange: mapRevenueRange((_b = record.revenue_range) !== null && _b !== void 0 ? _b : record.revenueRange),
        companyLid,
        companyLids,
        isBlockedForShow: Boolean(record.isBlockedForShow),
        lushaCompanyId: asNullableNumber((_c = record.lusha_company_id) !== null && _c !== void 0 ? _c : record.lushaCompanyId),
        topics: mapTopics(record.topics),
        fundingSummary: formatFundingSummary(record.funding_summary) || null,
        listId: asString(record.listId) || null,
        metadata: mapMetadata(record.metadata),
    };
};
export const resolveCompany = (companiesMap, company) => {
    if (company == null || company === '') {
        return undefined;
    }
    if (typeof company === 'number') {
        return companiesMap[String(company)];
    }
    if (typeof company === 'string') {
        return companiesMap[company];
    }
    const key = getCompanyMapKey(company);
    if (key && companiesMap[key]) {
        return companiesMap[key];
    }
    if (company.companyLid != null) {
        return companiesMap[String(company.companyLid)];
    }
    return undefined;
};
